/*
  Where a spot sits in its document (2.4.9). The deliverable view draws it on the page image, and the same
  place is told in words for a spot that has no page (a sheet and a cell, an XML id, a heading path).
*/

import type { Locator } from "../../../api/types";

/**
 * The pictures of the pages are drawn at `DeliverableView.page_dpi` dots to the inch (the setting `intake.render_dpi`),
 * and a box of a PDF word or line is in hundredths of a point (72 points to the inch) from the top left corner. This is
 * the starting value of the setting, used when a view does not say.
 */
export const PAGE_RENDER_DPI = 150;

export interface Size {
  width: number;
  height: number;
}

/** A place on a page as shares of the picture's width and height (0 to 100), so it follows the picture's size on screen. */
export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function toRect(x0: number, y0: number, x1: number, y1: number, size: Size): Rect | null {
  if (!(size.width > 0 && size.height > 0)) return null;
  const left = clamp(Math.min(x0, x1), 0, size.width);
  const right = clamp(Math.max(x0, x1), 0, size.width);
  const top = clamp(Math.min(y0, y1), 0, size.height);
  const bottom = clamp(Math.max(y0, y1), 0, size.height);
  if (right <= left || bottom <= top) return null;
  return {
    left: (left / size.width) * 100,
    top: (top / size.height) * 100,
    width: ((right - left) / size.width) * 100,
    height: ((bottom - top) / size.height) * 100,
  };
}

/**
 * The rectangle of a locator on the picture of its page, or null when it names no area (a cell, an XML id) or no page.
 * `region` is in pixels of the page picture; `box` is in hundredths of a point and is turned into pixels by the
 * dots to the inch of the picture.
 */
export function locatorRect(locator: Locator, picture: Size, dpi: number = PAGE_RENDER_DPI): Rect | null {
  if (locator.region) {
    const [x0, y0, x1, y1] = locator.region;
    return toRect(x0, y0, x1, y1, picture);
  }
  if (locator.box) {
    const scale = dpi / 7200; // hundredths of a point to pixels
    const [x0, y0, x1, y1] = locator.box;
    return toRect(x0 * scale, y0 * scale, x1 * scale, y1 * scale, picture);
  }
  return null;
}

/** The pixels of a drag on the picture as a region of the page picture (whole numbers, corners in order). */
export function regionFromDrag(start: { x: number; y: number }, end: { x: number; y: number }, picture: Size): [number, number, number, number] | null {
  const x0 = clamp(Math.round(Math.min(start.x, end.x)), 0, picture.width);
  const x1 = clamp(Math.round(Math.max(start.x, end.x)), 0, picture.width);
  const y0 = clamp(Math.round(Math.min(start.y, end.y)), 0, picture.height);
  const y1 = clamp(Math.round(Math.max(start.y, end.y)), 0, picture.height);
  // A press that hardly moved is a click, not an area.
  if (x1 - x0 < 4 || y1 - y0 < 4) return null;
  return [x0, y0, x1, y1];
}

/** Where a spot is, in words: "page 3", "sheet Data, cell B4", "under Pump data, line 12", "object P-101". */
export function locatorWords(locator: Locator): string {
  const parts: string[] = [];
  if (locator.page != null) parts.push(`page ${locator.page}`);
  if (locator.sheet) parts.push(`sheet ${locator.sheet}`);
  if (locator.cell) parts.push(`cell ${locator.cell}`);
  else if (locator.range) parts.push(`cells ${locator.range}`);
  if (locator.heading_path && locator.heading_path.length > 0) parts.push(`under ${locator.heading_path.join(" › ")}`);
  if (locator.line_no != null) parts.push(`line ${locator.line_no}`);
  if (locator.xml_id) parts.push(`object ${locator.xml_id}`);
  else if (locator.xml_path) parts.push(`at ${locator.xml_path}`);
  if (locator.tile) parts.push(`tile ${locator.tile}`);
  if (parts.length === 0 && (locator.box || locator.region)) parts.push("a marked area");
  return parts.join(", ") || "place not recorded";
}

/** The page a spot is on, when it is on one. */
export function pageOf(locator: Locator): number | null {
  return locator.page ?? null;
}
